import React from 'react';
import { Box, Card, Skeleton, Stack, Typography } from '@mui/material';
import { tint } from '../../theme';

type Tone = 'primary' | 'success' | 'warning' | 'error' | 'info' | 'secondary';

interface StatCardProps {
  label: string;
  value: React.ReactNode;
  /** Short qualifier under the figure, e.g. the period it covers. */
  hint?: string;
  icon?: React.ReactNode;
  tone?: Tone;
  loading?: boolean;
}

/**
 * One headline figure. The tone only tints the icon chip — the number itself
 * stays in the primary text colour so a row of tiles reads as one scale
 * instead of six competing colours.
 */
export const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  hint,
  icon,
  tone = 'primary',
  loading = false,
}) => (
  <Card sx={{ p: 2.5, height: '100%' }}>
    <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between">
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="overline"
          sx={{ color: 'text.secondary', display: 'block', mb: 1 }}
        >
          {label}
        </Typography>
        {loading ? (
          <Skeleton variant="text" width={88} height={40} />
        ) : (
          <Typography
            className="tnum"
            sx={{ fontSize: '1.75rem', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.15 }}
          >
            {value}
          </Typography>
        )}
        {hint && (
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.75 }}>
            {hint}
          </Typography>
        )}
      </Box>
      {icon && (
        <Box
          aria-hidden="true"
          sx={{
            display: 'grid',
            placeItems: 'center',
            width: 40,
            height: 40,
            borderRadius: 2,
            flexShrink: 0,
            color: `${tone}.main`,
            bgcolor: (theme) => tint(theme, `${tone}.main`, 0.12),
            '& svg': { fontSize: 20 },
          }}
        >
          {icon}
        </Box>
      )}
    </Stack>
  </Card>
);

export default StatCard;
